import {
  AttachDocumentDto,
  CreateMeetingDto,
  DetachDocumentDto,
  GetMeetingsParams,
  MeetingResponse,
  UpdateMeetingDto,
} from '@/api';
import { meetingKeys } from '@shared/api/query-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';
import { useTranslation } from 'react-i18next';
import { meetingsApi } from './meetings';
import { meetingQueries } from './queries';

export const useMeetings = (auditId?: string, params?: GetMeetingsParams, enabled?: boolean) =>
  useQuery({
    ...meetingQueries.list(auditId, params),
    enabled: Boolean(auditId) && enabled,
  });

export const useAllMeetings = (auditId?: string) =>
  useQuery({
    ...meetingQueries.allForAudit(auditId),
    enabled: Boolean(auditId),
  });

export const useMeeting = (auditId?: string, meetingId?: string | number, enabled?: boolean) =>
  useQuery({
    ...meetingQueries.detail(auditId, meetingId),
    enabled: Boolean(auditId && meetingId && enabled),
  });

export const useCreateMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, data }: { auditId: string; data: CreateMeetingDto }) =>
      meetingsApi.createMeeting(auditId, data),
    onSuccess: (_data, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.lists(auditId) });
    },
  });
};

export const useUpdateMeeting = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('dashboard', { keyPrefix: 'meetings' });

  return useMutation({
    /**
     * Meeting edits autosave (field blur, debounced editor typing, date pickers, file
     * upload), so a failure has no submit button to report on — it has to speak for
     * itself. `errorMessage` opts into the centralized MutationCache toast and wins over
     * the backend body, which for the 5xx this guards is the untranslated
     * "An unexpected error occurred.". Call sites must still swallow the rejection:
     * an escaped one becomes a dev-overlay crash that blocks the whole view.
     */
    meta: { errorMessage: t('update_failed') },
    mutationFn: ({
      auditId,
      meetingId,
      data,
      config,
    }: {
      auditId: string;
      meetingId: string | number;
      data: UpdateMeetingDto;
      config?: AxiosRequestConfig;
    }) => meetingsApi.updateMeeting(auditId, meetingId, data, config),
    onSuccess: (_data, { auditId, meetingId }) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.lists(auditId) });
      queryClient.invalidateQueries({ queryKey: meetingKeys.detail(auditId, meetingId) });
    },
  });
};

/**
 * The places that cite this meeting. Its own root (`meetingKeys.backlinks`), so the
 * detail/list invalidation after every edit leaves it alone; it refreshes on another
 * entity's sync and after a mention write. A count above zero is what earns the
 * citations counter and the disabled Delete — never the other way round: a refusal
 * can name citations a client cannot see.
 */
export const useMeetingBacklinks = (auditId?: string, meetingId?: string | number) =>
  useQuery({
    queryKey: meetingKeys.backlinks(auditId, meetingId),
    queryFn: () => meetingsApi.getBacklinks(auditId as string, meetingId as string | number),
    enabled: Boolean(auditId && meetingId),
  });

export const useDeleteMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, meetingId }: { auditId: string; meetingId: string | number }) =>
      meetingsApi.deleteMeeting(auditId, meetingId),
    onSuccess: (_data, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.lists(auditId) });
    },
  });
};

export const useAttachDocumentToMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      auditId,
      meetingId,
      data,
    }: {
      auditId: string;
      meetingId: string | number;
      data: AttachDocumentDto;
    }) => meetingsApi.attachDocument(auditId, meetingId, data),
    onSuccess: (_result, { auditId, meetingId }) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.lists(auditId) });
      queryClient.invalidateQueries({ queryKey: meetingKeys.detail(auditId, meetingId) });
    },
  });
};

export const useDetachDocumentFromMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      auditId,
      meetingId,
      data,
    }: {
      auditId: string;
      meetingId: string | number;
      data: DetachDocumentDto;
    }) => meetingsApi.detachDocument(auditId, meetingId, data),
    onMutate: async ({ auditId, meetingId, data }) => {
      await queryClient.cancelQueries({ queryKey: meetingKeys.detail(auditId, meetingId) });

      const previousMeeting = queryClient.getQueryData<MeetingResponse | undefined>(
        meetingKeys.detail(auditId, meetingId),
      );

      queryClient.setQueryData<MeetingResponse | undefined>(
        meetingKeys.detail(auditId, meetingId),
        (old: MeetingResponse | undefined) => {
          if (!old) return old;

          if (old.meeting) {
            return {
              ...old,
              meeting: {
                ...old.meeting,
                files: (old.meeting.files ?? []).filter((f) => f.id !== data.documentId),
              },
            };
          }
        },
      );

      return { previousMeeting };
    },
    onSettled: (_result, _error, { auditId, meetingId }) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.detail(auditId, meetingId) });
    },
  });
};

export const useAddMeetingComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      auditId,
      meetingId,
      data,
    }: {
      auditId: string;
      meetingId: string | number;
      data: { text: string };
    }) => meetingsApi.addComment(auditId, meetingId, data),
    onSuccess: (_result, { auditId, meetingId }) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.lists(auditId) });
      queryClient.invalidateQueries({ queryKey: meetingKeys.detail(auditId, meetingId) });
    },
  });
};
